import {
  useEffect,
  useLayoutEffect,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
} from "react"

// Dragging the voice mini-player between its resting slots.
//
// Whether it has a sideways choice is a question about ROOM: a card that fills
// its track can only travel vertically, because every horizontal slot puts it
// in the same place. On a phone only the folded 64px circle has a side to
// choose, and only two: left and right. On a desktop the card is capped well
// short of its track, so the card and its folded circle rest in one of three
// columns — centered like the audio guide player, where every call starts, or
// pushed into a corner to get it off something.
//
// Position stays in CSS: the dock owns the anchor formulas, and the top slot
// reads --header-height, which reacts to the page with no JS (custom properties
// have no change event to observe). So this hook only ever writes a TRANSFORM,
// and hands the resolved slot back on release. Between drags there is no
// transform and the CSS is unopposed.

export type PipVertical = "top" | "bottom"
export type PipHorizontal = "left" | "center" | "right"

/**
 * How many columns this surface offers — see the note above.
 *
 * 1 is "sideways means nothing here": the axis is dropped from the gesture, the
 * arrow keys leave it alone, and whatever `horizontal` says is carried through
 * untouched for when there IS a choice to make.
 */
export type PipColumns = 1 | 2 | 3

// Where the player rests. Deliberately does NOT carry whether it is collapsed:
// that is a phase every mount passes through on a timer (see useAutoCollapse),
// not a preference worth remembering, and storing it would mean a player that
// mounted already folded away.
//
// It is held for the length of a call rather than of a mount, since expanding
// unmounts the player — the voice slice owns it; this hook only resolves it.
export interface PipPosition {
  vertical: PipVertical
  // Read wherever the player has columns: always on a desktop, and on a phone
  // only while collapsed. Kept across a phone's expanded state so collapsing
  // puts the orb back on the side the user last left it rather than jumping it
  // across the screen.
  horizontal: PipHorizontal
}

// Where every NEW call starts, on every surface — not every appearance: within
// one call the corner the user dragged to survives (see the voice slice).
//
// Bottom because the card arrives from the overlay's orb flying down to it, and
// centered like the audio guide player. Where the card fills the row (a phone)
// center and left are the same place, and the folded circle, which has no
// center column, docks on the left: that is where the chip sits inside the
// expanded card, so the fold closes the card around the orb rather than
// sliding it across to the other corner.
export const DEFAULT_PIP_POSITION: PipPosition = {
  vertical: "bottom",
  horizontal: "center",
}

// How far the pointer must travel before this is a drag rather than a tap. The
// player's body and both of its controls are buttons, so every drag starts on
// something clickable, and the threshold is what keeps a slightly imprecise tap
// on Mute from becoming a two-pixel drag that swallows the click.
const DRAG_THRESHOLD_PX = 8

// The release snap is a damped spring, not a timed curve: seeded with the
// release offset and the finger's velocity, so a short hop settles quickly, a
// long swap takes proportionally longer, and a thrown player leaves at the speed
// it was thrown.
//
// The pull toward the slot, as an undamped natural frequency in rad/s.
// Together with the damping ratio this is the entire feel of the snap: raise
// it and everything arrives sooner.
const SNAP_OMEGA = 20
// Fraction of critical damping. At 0.9 a still release shows no visible
// overshoot (the first swing past the slot is ~0.15% of the travel), while a
// genuine throw still carries past and gets drawn back — whether that happens
// is decided by the seeded velocity, not baked into the curve.
const SNAP_DAMPING_RATIO = 0.9
// The seed is capped so the wildest flick swings at most ~40px past the slot.
// Beyond this, the extra speed was aim — it has already had its say in
// resolvePipSlot's projection — not intent for the landing.
const SNAP_MAX_VELOCITY_PX_MS = 2
// At rest means within half a pixel of the slot. Sets the settle time via the
// decay envelope — ln(travel / this) / (ζω) — and that logarithm is where the
// duration's distance scaling comes from.
const SNAP_REST_DELTA_PX = 0.5
const SNAP_MAX_DURATION_MS = 600
// One sample per 60Hz frame: the browser interpolates linearly between
// keyframes, so the snap plays as 60fps motion. The corners of the decaying
// sinusoid stay sub-pixel except on high-refresh displays during the fastest
// stretch of a hard throw — halve this if that ever reads as stepped.
const SNAP_SAMPLE_MS = 1000 / 60

// How far ahead a throw is credited, so the player lands where the gesture was
// HEADED rather than where the finger stopped.
//
// Deliberately shorter than the ~500ms an iOS-style deceleration model would
// project: the slots are halves of a screen rather than free coordinates, and
// an over-eager projection turns an ordinary drag that ended while still moving
// into a throw the user didn't make. At this constant a flick needs roughly
// 0.4px/ms to carry the player across a half it released 100px inside.
const FLICK_PROJECTION_MS = 250

// Frames shorter than this are dropped rather than divided by — a frame served
// in a millisecond turns a 2px wobble into 2px/ms of "throw".
const VELOCITY_SAMPLE_MIN_MS = 8
// A single frame's delta is noisy at exactly the moment it matters most, so
// each sample is eased into the last. Weighted toward the newest: this is a
// flick detector, and the end of the gesture is the part being detected.
const VELOCITY_SMOOTHING = 0.5
// A finger that has been still this long is not throwing anything, whatever it
// was doing before it stopped.
const VELOCITY_STALE_MS = 80

// px per millisecond, in viewport space — the same units and axes as the rect.
export interface PipVelocity {
  x: number
  y: number
}

const AT_REST: PipVelocity = { x: 0, y: 0 }

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

/**
 * What a released player is measured against — and it is not one box. Vertically
 * the slots are the screen's top and bottom, so the range is the visible height.
 * Horizontally the card never leaves its positioner, so the columns are slices
 * of THAT.
 *
 * The difference bites on a desktop, where the card is capped: a 448px card in a
 * 1040px track can only centre itself between 448 and 1040, and the middle of
 * that travel is the track's midpoint, not the window's. Against the window the
 * halves come out lopsided, and dropping left takes a haul at the screen edge.
 */
interface Bounds {
  height: number
  left: number
  right: number
}

// One live gesture. Mutated in place for its whole life — every field here is
// written from a pointer event or a frame, so allocating a new object per
// update would put garbage on exactly the path this hook keeps clear.
interface Gesture {
  pointerId: number
  startX: number
  startY: number
  offsetX: number
  offsetY: number
  passedThreshold: boolean
  // At the press for touch and pen, at the drag threshold for a mouse, for
  // opposite reasons — see onPointerDown and onPointerMove.
  captured: boolean
  // Smoothed px/ms, and the sample the next one is measured against.
  velocityX: number
  velocityY: number
  sampleX: number
  sampleY: number
  sampleAt: number
}

/**
 * Fold this frame's travel into the gesture's running velocity.
 *
 * Called from the frame that writes the transform rather than from the pointer
 * events, which is both cheaper and steadier: one sample per painted frame
 * instead of two or three per frame on a 120Hz digitiser, each over a dt long
 * enough to mean something.
 */
function sampleVelocity(gesture: Gesture, now: number) {
  const elapsed = now - gesture.sampleAt
  if (elapsed < VELOCITY_SAMPLE_MIN_MS) return

  const x = (gesture.offsetX - gesture.sampleX) / elapsed
  const y = (gesture.offsetY - gesture.sampleY) / elapsed
  gesture.velocityX += (x - gesture.velocityX) * VELOCITY_SMOOTHING
  gesture.velocityY += (y - gesture.velocityY) * VELOCITY_SMOOTHING
  gesture.sampleX = gesture.offsetX
  gesture.sampleY = gesture.offsetY
  gesture.sampleAt = now
}

/**
 * The velocity to credit a release with — nothing at all if the finger had
 * already come to rest. Frames stop when movement does, so the last sample sits
 * there looking current indefinitely: without this, "flick, hold, release"
 * would launch the player on a velocity measured seconds earlier.
 */
function releaseVelocity(gesture: Gesture, now: number): PipVelocity {
  if (now - gesture.sampleAt > VELOCITY_STALE_MS) return AT_REST
  return { x: gesture.velocityX, y: gesture.velocityY }
}

/**
 * Which slot a released player belongs to: the nearer half on each axis it may
 * travel, measured from where the throw was HEADING rather than where the
 * finger let go. Pure, and the only real decision in a drag, so a test can
 * reach it without the hook.
 *
 * Velocity folds into that comparison rather than overriding it — the centre is
 * projected forward by FLICK_PROJECTION_MS and the halves read off that. One
 * rule, so there is no threshold where behaviour changes character: a still
 * release projects nothing, and it defaults to at-rest for callers with no
 * velocity to offer (the keyboard).
 *
 * `horizontal` carries over untouched at one column, where the shape constrains
 * the drag to one axis and nothing sideways means anything.
 */
const HORIZONTAL_ORDER: PipHorizontal[] = ["left", "center", "right"]

export function resolvePipSlot(
  rect: Rect,
  bounds: Bounds,
  current: PipPosition,
  columns: PipColumns,
  velocity: PipVelocity = AT_REST
): PipPosition {
  const landingY = rect.top + rect.height / 2 + velocity.y * FLICK_PROJECTION_MS
  const vertical: PipVertical = landingY < bounds.height / 2 ? "top" : "bottom"

  if (columns === 1) {
    return { ...current, vertical }
  }

  const landingX = rect.left + rect.width / 2 + velocity.x * FLICK_PROJECTION_MS
  if (columns === 2) {
    const horizontal: PipHorizontal =
      landingX < (bounds.left + bounds.right) / 2 ? "left" : "right"
    return { ...current, vertical, horizontal }
  }

  // Three columns: the nearest of the card's three resting centers — flush
  // left, the track's middle, flush right.
  const half = rect.width / 2
  const centers: Record<PipHorizontal, number> = {
    left: bounds.left + half,
    center: (bounds.left + bounds.right) / 2,
    right: bounds.right - half,
  }
  const horizontal = HORIZONTAL_ORDER.reduce((nearest, slot) =>
    Math.abs(landingX - centers[slot]) < Math.abs(landingX - centers[nearest])
      ? slot
      : nearest
  )
  return { ...current, vertical, horizontal }
}

export interface SpringSnap {
  frames: Keyframe[]
  duration: number
}

/**
 * The travel of a release: a damped spring from the visual offset, and the
 * velocity the finger left, back to rest at the slot. Sampled into keyframes
 * rather than expressed as an easing because an easing scales one straight line
 * and this path curves — the axes are independent springs and a flick is rarely
 * aimed along the offset. Null when there is nothing worth playing.
 */
export function springSnapKeyframes(
  deltaX: number,
  deltaY: number,
  velocity: PipVelocity = AT_REST
): SpringSnap | null {
  // ζω, the decay rate of the envelope; ω√(1-ζ²), the ringing frequency.
  const decay = SNAP_DAMPING_RATIO * SNAP_OMEGA
  const ringing = SNAP_OMEGA * Math.sqrt(1 - SNAP_DAMPING_RATIO ** 2)

  // Seeds in px/s (the frequencies above are per second), capped per axis.
  const seed = (v: number) =>
    Math.max(-SNAP_MAX_VELOCITY_PX_MS, Math.min(SNAP_MAX_VELOCITY_PX_MS, v)) *
    1000

  // The underdamped closed form per axis:
  //   x(t) = e^(-ζωt) · (x₀·cos(ω_d·t) + b·sin(ω_d·t))
  const bX = (seed(velocity.x) + decay * deltaX) / ringing
  const bY = (seed(velocity.y) + decay * deltaY) / ringing

  // |x(t)| ≤ e^(-ζωt)·hypot(x₀, b), so the time for the larger axis's envelope
  // to fall under the rest threshold bounds the whole 2D motion.
  const travel = Math.max(Math.hypot(deltaX, bX), Math.hypot(deltaY, bY))
  if (travel <= SNAP_REST_DELTA_PX) return null

  const duration = Math.min(
    (Math.log(travel / SNAP_REST_DELTA_PX) / decay) * 1000,
    SNAP_MAX_DURATION_MS
  )

  // No explicit offsets: WAAPI spaces the frames evenly, which matches the
  // even sample times. Rounded to hundredths — sub-pixel beyond that, and it
  // keeps a value crossing zero from serializing in scientific notation.
  const count = Math.ceil(duration / SNAP_SAMPLE_MS)
  const frames: Keyframe[] = []
  for (let i = 0; i < count; i++) {
    const t = (i / count) * (duration / 1000)
    const envelope = Math.exp(-decay * t)
    const x =
      envelope * (deltaX * Math.cos(ringing * t) + bX * Math.sin(ringing * t))
    const y =
      envelope * (deltaY * Math.cos(ringing * t) + bY * Math.sin(ringing * t))
    frames.push({
      transform: `translate3d(${Math.round(x * 100) / 100}px, ${Math.round(y * 100) / 100}px, 0)`,
    })
  }
  // The last frame is the slot exactly, not the envelope's final whisper of it.
  frames.push({ transform: "translate3d(0, 0, 0)" })

  return { frames, duration }
}

/**
 * Measure what the player snaps within — see Bounds.
 *
 * `visualViewport` rather than `innerHeight` so a raised keyboard shrinks the
 * halves instead of stranding the midpoint behind it. The horizontal track is
 * the card's own parent, which is the contract the hook already has with its
 * caller: `ref` goes on the element the anchor CSS positions. Falls back to the
 * window for a card mounted on its own.
 */
function currentBounds(element: HTMLElement): Bounds {
  const visual = window.visualViewport
  const height = visual?.height ?? window.innerHeight
  const window_ = { height, left: 0, right: visual?.width ?? window.innerWidth }
  const track = element.parentElement
  if (!track) return window_

  const box = track.getBoundingClientRect()
  const style = getComputedStyle(track)
  const left = box.left + (parseFloat(style.paddingLeft) || 0)
  const right = box.right - (parseFloat(style.paddingRight) || 0)
  // A track with no measurable width is not a track — a parent that is
  // display:none, or a test environment that lays nothing out. Dividing the
  // columns by that width would put every release in the last one.
  return right > left ? { height, left, right } : window_
}

interface UsePipDragOptions {
  position: PipPosition
  onPositionChange: (next: PipPosition) => void
  // How many resting columns this surface has — see PipColumns.
  columns: PipColumns
  // A discrete interaction that has already finished — the arrow keys. The
  // pointer reports through onHoldChange instead, which brackets a gesture
  // rather than punctuating it.
  onInteraction?: () => void
  // True while a finger is on the player, false when it leaves — including on a
  // gesture the browser cancels, and on a press that never moved. Brackets the
  // gesture rather than firing at the end of it, so the caller can suspend an
  // auto-collapse countdown for its whole length: a drag can easily outlast
  // five seconds, and a player that folded itself away under the finger moving it
  // would read as broken.
  onHoldChange?: (held: boolean) => void
}

interface UsePipDrag<T extends HTMLElement> {
  // Attach to the card. It has to be the element the anchor CSS positions,
  // because the FLIP measures it.
  ref: RefObject<T | null>
  // The only pointer handler the card needs. A gesture starts on the player
  // and is then followed on the window — see the effect in the hook.
  onPointerDown: (event: PointerEvent<T>) => void
  // Arrow keys move the player between slots — the keyboard's version of the
  // drag, since a drag has no equivalent for anyone who can't perform one.
  onKeyDown: (event: KeyboardEvent<T>) => void
  // Swallows the click that ends a drag, so releasing over Mute doesn't toggle
  // it. Capture phase, so it runs before the button's own handler.
  onClickCapture: (event: MouseEvent<T>) => void
}

export function usePipDrag<T extends HTMLElement = HTMLElement>({
  position,
  onPositionChange,
  columns,
  onInteraction,
  onHoldChange,
}: UsePipDragOptions): UsePipDrag<T> {
  const ref = useRef<T | null>(null)
  // A gesture's whole lifetime lives in this ref rather than state: pointermove
  // fires faster than the display refreshes and each one feeds a transform,
  // which must not cost a React render.
  const gestureRef = useRef<Gesture | null>(null)
  // Set between the release and the re-render that carries the new slot, so the
  // layout effect below knows a FLIP is owed, where the player visually was when
  // the finger left it, and how fast it was still moving — the travel is
  // seeded with that velocity, so the player leaves at the speed it was thrown.
  const pendingFlipRef = useRef<{ rect: Rect; velocity: PipVelocity } | null>(
    null
  )
  const suppressClickRef = useRef(false)
  // Tears down the window subscription below. Non-null exactly while a gesture
  // is in flight.
  const unfollowRef = useRef<(() => void) | null>(null)
  // The frame a pending transform write is scheduled on, if any.
  const frameRef = useRef<number | null>(null)
  // The release snap, while it is running. A WAAPI animation outranks inline
  // styles, so one still in flight would swallow the next drag's transforms and
  // leave the card sitting still under a moving finger for up to a third of a
  // second — the drag has to be able to interrupt it.
  const snapRef = useRef<Animation | null>(null)

  // Hands back the compositing layer taken at the press. Declines if a gesture
  // is live: the snap's own cancellation fires this asynchronously, and by then
  // the drag that cancelled it has already asked for a layer of its own.
  const releaseLayer = () => {
    if (gestureRef.current) return
    const element = ref.current
    if (element) element.style.willChange = ""
  }

  // Pointer events are not paced by the display. Phones deliver them at the
  // touchscreen's rate — often 120Hz against a 60Hz screen, and Chrome's
  // coalescing means a burst can arrive together — so writing the transform per
  // event does the work two or three times over for one painted frame, and each
  // write invalidates the card's style whether or not a frame follows.
  //
  // Everything the write needs already lives in the gesture ref, so a frame can
  // simply read the latest values: no queue, no per-event work beyond arithmetic
  // and a flag.
  const writeTransform = () => {
    frameRef.current = null
    const gesture = gestureRef.current
    const element = ref.current
    if (!gesture || !element) return
    sampleVelocity(gesture, performance.now())
    element.style.transform = `translate3d(${gesture.offsetX}px, ${gesture.offsetY}px, 0)`
  }

  const scheduleTransform = () => {
    if (frameRef.current !== null) return
    frameRef.current = requestAnimationFrame(writeTransform)
  }

  // The release has to measure where the card actually is, and a scheduled
  // write that hasn't run yet would leave the DOM a frame behind the finger —
  // enough, at the edge of a slot, to snap it back to the one it just left.
  const flushTransform = () => {
    if (frameRef.current === null) return
    cancelAnimationFrame(frameRef.current)
    writeTransform()
  }

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      // Unmounting mid-gesture: nothing else will ever call endGesture.
      unfollowRef.current?.()
    },
    []
  )

  // Second half of the FLIP. The caller owns the slot, so the element only
  // reaches its new anchor after their re-render — this is the first moment
  // both rects are knowable, and it runs before paint, so the jump is never
  // seen.
  useLayoutEffect(() => {
    const pending = pendingFlipRef.current
    pendingFlipRef.current = null
    const element = ref.current
    if (!pending || !element) return

    const after = element.getBoundingClientRect()
    snapRef.current = animateFrom(
      element,
      pending.rect.left - after.left,
      pending.rect.top - after.top,
      pending.velocity,
      releaseLayer
    )
  }, [position])

  // Move to a slot and animate the travel. Drag release and arrow keys both
  // land here, so the player travels the same way however it was told to.
  //
  // `before` is passed in by the drag, which must read the rect while the
  // finger's offset is still applied — by the time this runs the transform is
  // cleared, and measuring here would start the animation at the old dock
  // rather than under the finger that just left. The keyboard has no transform
  // to lose and no velocity to offer, so it measures here and springs from
  // rest.
  const moveTo = (
    next: PipPosition,
    before?: Rect,
    velocity: PipVelocity = AT_REST
  ) => {
    const element = ref.current
    if (
      !element ||
      (next.vertical === position.vertical &&
        next.horizontal === position.horizontal)
    ) {
      return false
    }

    const { top, left, width, height } =
      before ?? element.getBoundingClientRect()
    pendingFlipRef.current = { rect: { top, left, width, height }, velocity }
    onPositionChange(next)
    return true
  }

  // A gesture is followed on the window rather than on the card, for one
  // window: a MOUSE cannot take capture until the drag is real (see
  // onPointerMove), so between the press and the 8px threshold nothing holds
  // the sequence to the card, and a drag starting near an edge would leave
  // without the card ever seeing a move or a release. Touch and pen capture at
  // the press and never need it, but cost nothing by sharing the path.
  //
  // Subscribed for the length of a GESTURE, not of the mount. The player is on
  // screen for the whole call, and a window pointermove listener that outlives
  // the gesture makes every mouse move anywhere on the page run a handler whose
  // only job is to discover there is no gesture.
  //
  // Reached through a ref because the subscription outlives the renders under
  // it: the handlers close over the current position and column count, and a
  // pair captured at the press would resolve the release against whatever was
  // true when the finger went down. Filled by the effect below, which has run
  // long before any pointer event can arrive.
  const handlersRef = useRef<{
    move: (event: PointerMoveLike) => void
    end: (event: { pointerId: number }) => void
  } | null>(null)

  const followOnWindow = () => {
    if (unfollowRef.current) return
    const move = (event: globalThis.PointerEvent) =>
      handlersRef.current?.move(event)
    const end = (event: globalThis.PointerEvent) =>
      handlersRef.current?.end(event)
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", end)
    window.addEventListener("pointercancel", end)
    unfollowRef.current = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", end)
      window.removeEventListener("pointercancel", end)
      unfollowRef.current = null
    }
  }

  const onPointerDown = (event: PointerEvent<T>) => {
    // Only a gesture that has taken capture can be sure of seeing its own end.
    // One that has not can be abandoned off the card — a press within the
    // threshold that wandered over the edge, whose pointerup the card never
    // sees — and if that locked the player out, it would be locked out for
    // good. A fresh press supersedes it.
    if (gestureRef.current?.captured) return
    // Primary button for a mouse; any contact for touch and pen.
    if (event.pointerType === "mouse" && event.button !== 0) return

    const element = ref.current
    if (!element) return

    // Take the card back off the snap if one is still running. Left alone it
    // would keep animating over the drag's own transforms — WAAPI outranks
    // inline styles — so grabbing the player straight after dropping it would
    // feel dead for a third of a second.
    snapRef.current?.cancel()
    snapRef.current = null

    // Whatever the last gesture armed, this one supersedes. Without this a
    // suppression that never got its click — a cancelled pointer sequence, a
    // release the browser didn't follow with one — would sit here and swallow
    // the next real tap on Mute instead.
    suppressClickRef.current = false

    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: 0,
      offsetY: 0,
      passedThreshold: false,
      captured: false,
      velocityX: 0,
      velocityY: 0,
      sampleX: 0,
      sampleY: 0,
      sampleAt: performance.now(),
    }
    onHoldChange?.(true)
    followOnWindow()

    // Touch and pen claim the pointer at the press. The retargeting problem
    // that moved capture to the threshold is a MOUSE compatibility-event
    // problem and nothing else — a synthesised tap hit-tests its own point, so
    // touch never had it — and a touch drag wants the gesture claimed at the
    // earliest moment the browser will accept the claim.
    if (event.pointerType !== "mouse") {
      gestureRef.current.captured = true
      element.setPointerCapture(event.pointerId)
    }

    // Promoted at the press, not on the first moved frame: the card's blurred
    // three-layer shadow makes a compositing layer cost a full raster, and
    // asking for it as the finger starts moving puts that in the one frame the
    // user judges responsiveness by. Written directly rather than through a
    // `dragging` class, because the render carrying it would land at the same
    // bad moment — a gesture here costs no renders at all.
    element.style.willChange = "transform"
  }

  const onPointerMove = (event: PointerMoveLike) => {
    const gesture = gestureRef.current
    const element = ref.current
    if (!gesture || !element || event.pointerId !== gesture.pointerId) return

    // With one column the card spans its track, so horizontal travel would move
    // it without ever changing where it lands. Drop the axis rather than let
    // the player slide somewhere it can't rest.
    const offsetX = columns === 1 ? 0 : event.clientX - gesture.startX
    const offsetY = event.clientY - gesture.startY

    if (!gesture.passedThreshold) {
      if (Math.hypot(offsetX, offsetY) < DRAG_THRESHOLD_PX) return
      gesture.passedThreshold = true
      // Capture from here, so the gesture keeps landing on the player even
      // while the pointer is over the sheet underneath — a release over a modal
      // sheet is otherwise an outside press, which dismisses it.
      //
      // For a MOUSE the press is NOT a safe moment. Capture retargets the
      // compatibility mouse events too, and a mouse `click` fires at the
      // nearest common ancestor of its mousedown and mouseup targets — so
      // capture at pointerdown moves only the up half, and every click on a
      // control inside the card lands on the card. Touch and pen claimed the
      // pointer at the press already (see onPointerDown), so this is their
      // no-op. The cost for a mouse is the 8px before it, which nothing holds
      // to the card — hence the window listeners below.
      if (!gesture.captured) {
        gesture.captured = true
        element.setPointerCapture(event.pointerId)
      }
    }

    gesture.offsetX = offsetX
    gesture.offsetY = offsetY
    scheduleTransform()
  }

  const endGesture = (event: { pointerId: number }) => {
    const gesture = gestureRef.current
    if (!gesture || event.pointerId !== gesture.pointerId) return

    const element = ref.current
    flushTransform()
    gestureRef.current = null
    unfollowRef.current?.()
    onHoldChange?.(false)

    // Capture releases itself on pointerup and pointercancel, so there is
    // nothing to hand back here.
    if (!element) return
    if (!gesture.passedThreshold) {
      // A tap. Nothing will animate, so the layer promised at the press is
      // owed back here rather than by animateFrom.
      element.style.willChange = ""
      return
    }

    // The click closing this gesture is still to come; it must not reach the
    // control underneath.
    suppressClickRef.current = true

    // Read once, while the offset is still on the element: this rect is both
    // what decides the slot and where the travel has to start from. The
    // velocity likewise — it picks the slot and then seeds the travel there.
    const before = element.getBoundingClientRect()
    const velocity = releaseVelocity(gesture, performance.now())
    const next = resolvePipSlot(
      before,
      currentBounds(element),
      position,
      columns,
      velocity
    )
    element.style.transform = ""

    if (!moveTo(next, before, velocity)) {
      // Released in the slot it started in, so no re-render is coming and the
      // layout effect will never run. Spring the offset off here instead.
      snapRef.current = animateFrom(
        element,
        gesture.offsetX,
        gesture.offsetY,
        velocity,
        releaseLayer
      )
    }
  }

  useEffect(() => {
    handlersRef.current = { move: onPointerMove, end: endGesture }
  })

  // The keyboard equivalent of the drag, for anyone who can't perform one. The
  // card is a div, but arrow keys do nothing on the buttons inside it, so they
  // reach this by bubbling from whichever control has focus.
  const onKeyDown = (event: KeyboardEvent<T>) => {
    let next: PipPosition | null = null
    if (event.key === "ArrowUp") next = { ...position, vertical: "top" }
    if (event.key === "ArrowDown") next = { ...position, vertical: "bottom" }
    // Sideways only means something where there is more than one column. Left
    // unhandled otherwise rather than quietly setting a preference with nothing
    // to show for it. With two columns an arrow goes straight to that side;
    // with three it steps one column.
    if (columns === 2) {
      if (event.key === "ArrowLeft") next = { ...position, horizontal: "left" }
      if (event.key === "ArrowRight") {
        next = { ...position, horizontal: "right" }
      }
    }
    if (columns === 3) {
      const index = HORIZONTAL_ORDER.indexOf(position.horizontal)
      const step =
        event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0
      const horizontal = HORIZONTAL_ORDER[index + step]
      if (step !== 0 && horizontal) next = { ...position, horizontal }
    }
    if (!next) return

    event.preventDefault()
    onInteraction?.()
    moveTo(next)
  }

  const onClickCapture = (event: MouseEvent<T>) => {
    if (!suppressClickRef.current) return
    suppressClickRef.current = false
    event.preventDefault()
    event.stopPropagation()
  }

  return {
    ref,
    onPointerDown,
    onKeyDown,
    onClickCapture,
  }
}

// What onPointerMove reads, which both the React event and the native one
// satisfy — the card starts a gesture, the window carries it.
interface PointerMoveLike {
  pointerId: number
  clientX: number
  clientY: number
}

// Play an element from a visual offset — and the velocity the finger released
// it with — back to where the layout already puts it. The element is at its
// resting transform before this is called, so the animation is purely
// presentational and there is nothing to clean up if it is interrupted —
// including by the next drag.
function animateFrom(
  element: HTMLElement,
  deltaX: number,
  deltaY: number,
  velocity: PipVelocity,
  // Called once the card is finally at rest, however that came about. This is
  // the last thing that happens after a gesture, so it is where the compositing
  // layer the press asked for is handed back — including on the paths where
  // nothing animates at all. A `will-change` left behind pins a layer for the
  // rest of the player's life, which is the exact cost the property exists to be
  // borrowed for rather than kept.
  onSettled: () => void
): Animation | null {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    onSettled()
    return null
  }
  // The animation is decoration over a move that has already happened in the
  // layout, so anywhere the Web Animations API is missing simply arrives
  // without it.
  if (typeof element.animate !== "function") {
    onSettled()
    return null
  }

  const snap = springSnapKeyframes(deltaX, deltaY, velocity)
  if (!snap) {
    onSettled()
    return null
  }

  const animation = element.animate(snap.frames, { duration: snap.duration })
  animation.onfinish = onSettled
  // Interrupted by the next gesture, which has already asked for its own layer
  // — onSettled knows to leave that one alone.
  animation.oncancel = onSettled
  return animation
}
